// ./src/hooks/useTireSets.ts
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import {
  TireSet,
  TireSetService,
  TireSetInput,
  TireSetSwapInput,
  TireSetSwapData,
  TireSetFilter,
  TIRE_SET_STATUS,
} from "@/utils/api";
import { useApp } from "@/contexts";
import { ApiError } from "@/utils";
import { Pagination, SortItem } from "@/utils/api/commonApiTypes";

// ============================================
// Types
// ============================================

export interface UseTireSetsOptions {
  /** Auto-load data on mount (default: false) */
  autoLoad?: boolean;
  /** Initial filter to apply */
  initialFilter?: TireSetFilter;
  /** Initial pagination settings */
  initialPagination?: Pagination;
  /** Initial sort settings */
  initialSortBy?: SortItem[];
  /** Car ID to filter by */
  carId?: string;
}

export interface UseTireSetsReturn {
  // Data state
  tireSets: TireSet[];
  selectedTireSet: TireSet | null;
  isLoading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  isRemoving: boolean;
  isSwapping: boolean;

  // Filter and pagination
  filter: TireSetFilter;
  pagination: Pagination;
  sortBy: SortItem[];
  setFilter: (filter: TireSetFilter) => void;
  setPagination: (pagination: Pagination) => void;
  setSortBy: (sortBy: SortItem[]) => void;

  // Core operations
  list: (
    filter?: TireSetFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ) => Promise<OpResult>;
  get: (id: string) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: TireSetInput) => Promise<OpResult>;
  update: (id: string, params: TireSetInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;
  swap: (params: TireSetSwapInput) => Promise<OpResult>;

  // Convenience methods
  refresh: () => Promise<OpResult>;
  select: (tireSet: TireSet | null) => void;
  clearSelection: () => void;

  // Helper operations
  reset: () => void;

  // Derived data helpers
  /** Get the active tire set for a specific car */
  getActiveForCar: (carId: string) => TireSet | undefined;
  /** Get all stored tire sets for a specific car */
  getStoredForCar: (carId: string) => TireSet[];

  // Error handling
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_PAGINATION: Pagination = {
  page: 1,
  pageSize: 50,
};

const DEFAULT_SORT_BY: SortItem[] = [
  { name: "name", order: "ASC" },
];

// ============================================
// Hook Implementation
// ============================================

export const useTireSets = (
  options: UseTireSetsOptions = {}
): UseTireSetsReturn => {
  const {
    autoLoad = false,
    initialFilter = {},
    initialPagination = DEFAULT_PAGINATION,
    initialSortBy = DEFAULT_SORT_BY,
    carId,
  } = options;

  // Get user from AppContext
  const { user } = useApp();
  const userId = user?.id ?? null;

  const previousUserIdRef = useRef<string | null | undefined>(undefined);
  const userIdRef = useRef<string | null>(userId);

  // Update userIdRef when userId changes
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // Data state
  const [tireSets, setTireSets] = useState<TireSet[]>([]);
  const [selectedTireSet, setSelectedTireSet] = useState<TireSet | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);

  // Filter and pagination state
  const [filter, setFilter] = useState<TireSetFilter>(() => ({
    ...initialFilter,
    ...(carId ? { carId: [carId] } : {}),
  }));
  const [pagination, setPagination] = useState<Pagination>(initialPagination);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSortBy);

  // Error handling
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setTireSets([]);
    setSelectedTireSet(null);
    setLastError(null);
    setIsLoading(false);
    setIsLoaded(false);
    setIsCreating(false);
    setIsUpdating(false);
    setIsRemoving(false);
    setIsSwapping(false);
  }, []);

  // ==========================================
  // Core Operations
  // ==========================================

  // List tire sets
  const list = useCallback(
    async (
      filterOverride?: TireSetFilter,
      paginationOverride?: Pagination,
      sortByOverride?: SortItem[]
    ): Promise<OpResult> => {
      setIsLoading(true);
      setLastError(null);

      try {
        const effectiveFilter = filterOverride ?? filter;
        const effectivePagination = paginationOverride ?? pagination;
        const effectiveSortBy = sortByOverride ?? sortBy;

        const result = await TireSetService.list(
          effectiveFilter,
          effectivePagination,
          effectiveSortBy
        );

        if (result.code === OP_RESULT_CODES.OK) {
          const data = (result.data as TireSet[]) || [];
          setTireSets(data);
          setIsLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load tire sets"));
        setIsLoaded(true);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load tire sets"));
        setIsLoaded(true);
        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [filter, pagination, sortBy]
  );

  // Get single tire set
  const get = useCallback(async (id: string): Promise<OpResult> => {
    setIsLoading(true);
    setLastError(null);

    try {
      const result = await TireSetService.get(id);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = Array.isArray(result.data)
          ? (result.data as TireSet[])[0]
          : (result.data as TireSet);
        setSelectedTireSet(data);
        setLastError(null);
        return result;
      }

      setLastError(ApiError.fromResult(result, "Failed to load tire set"));
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(ApiError.fromException(error, "Failed to load tire set"));
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get multiple tire sets
  const getMany = useCallback(async (ids: string[]): Promise<OpResult> => {
    setIsLoading(true);
    setLastError(null);

    try {
      const result = await TireSetService.getMany(ids);

      if (result.code === OP_RESULT_CODES.OK) {
        setLastError(null);
        return result;
      }

      setLastError(ApiError.fromResult(result, "Failed to load tire sets"));
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(ApiError.fromException(error, "Failed to load tire sets"));
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Create tire set
  const create = useCallback(
    async (params: TireSetInput): Promise<OpResult> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await TireSetService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newTireSet = Array.isArray(result.data)
            ? (result.data as TireSet[])[0]
            : (result.data as TireSet);

          // Add to list
          setTireSets((prev) => [newTireSet, ...prev]);
          setSelectedTireSet(newTireSet);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to create tire set"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to create tire set"));
        return result;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update tire set
  const update = useCallback(
    async (id: string, params: TireSetInput): Promise<OpResult> => {
      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await TireSetService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedTireSet = Array.isArray(result.data)
            ? (result.data as TireSet[])[0]
            : (result.data as TireSet);

          // Update in list
          setTireSets((prev) =>
            prev.map((ts) => (ts.id === id ? updatedTireSet : ts))
          );

          // Update selected if it's the same
          setSelectedTireSet((prev) =>
            prev?.id === id ? updatedTireSet : prev
          );

          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to update tire set"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to update tire set"));
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    []
  );

  // Remove tire set
  const remove = useCallback(async (id: string): Promise<OpResult> => {
    setIsRemoving(true);
    setLastError(null);

    try {
      const result = await TireSetService.remove(id);

      if (result.code === OP_RESULT_CODES.OK) {
        // Remove from list
        setTireSets((prev) => prev.filter((ts) => ts.id !== id));

        // Clear selection if removed
        setSelectedTireSet((prev) => (prev?.id === id ? null : prev));

        setLastError(null);
        return result;
      }

      setLastError(ApiError.fromResult(result, "Failed to remove tire set"));
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(ApiError.fromException(error, "Failed to remove tire set"));
      return result;
    } finally {
      setIsRemoving(false);
    }
  }, []);

  // Remove multiple tire sets
  const removeMany = useCallback(async (ids: string[]): Promise<OpResult> => {
    setIsRemoving(true);
    setLastError(null);

    try {
      const result = await TireSetService.removeMany(ids);

      if (result.code === OP_RESULT_CODES.OK) {
        const idsSet = new Set(ids);

        // Remove from list
        setTireSets((prev) => prev.filter((ts) => !idsSet.has(ts.id!)));

        // Clear selection if removed
        setSelectedTireSet((prev) =>
          prev?.id && idsSet.has(prev.id) ? null : prev
        );

        setLastError(null);
        return result;
      }

      setLastError(ApiError.fromResult(result, "Failed to remove tire sets"));
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(ApiError.fromException(error, "Failed to remove tire sets"));
      return result;
    } finally {
      setIsRemoving(false);
    }
  }, []);

  // Swap tire sets
  const swap = useCallback(
    async (params: TireSetSwapInput): Promise<OpResult> => {
      setIsSwapping(true);
      setLastError(null);

      try {
        const result = await TireSetService.swap(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const swapData = Array.isArray(result.data)
            ? (result.data as TireSetSwapData[])[0]
            : (result.data as TireSetSwapData);

          // Update both sets in the local list
          setTireSets((prev) =>
            prev.map((ts) => {
              if (swapData.installedSet && ts.id === swapData.installedSet.id) {
                return swapData.installedSet;
              }
              if (swapData.storedSet && ts.id === swapData.storedSet.id) {
                return swapData.storedSet;
              }
              return ts;
            })
          );

          // Update selected if it was one of the swapped sets
          setSelectedTireSet((prev) => {
            if (!prev) return prev;
            if (swapData.installedSet && prev.id === swapData.installedSet.id) {
              return swapData.installedSet;
            }
            if (swapData.storedSet && prev.id === swapData.storedSet.id) {
              return swapData.storedSet;
            }
            return prev;
          });

          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to swap tire sets"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to swap tire sets"));
        return result;
      } finally {
        setIsSwapping(false);
      }
    },
    []
  );

  // ==========================================
  // Convenience Methods
  // ==========================================

  // Refresh list with current filter/pagination
  const refresh = useCallback(async (): Promise<OpResult> => {
    return list();
  }, [list]);

  // Select a tire set
  const select = useCallback((tireSet: TireSet | null) => {
    setSelectedTireSet(tireSet);
  }, []);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedTireSet(null);
  }, []);

  // ==========================================
  // Derived Data Helpers
  // ==========================================

  const getActiveForCar = useCallback(
    (carId: string): TireSet | undefined => {
      return tireSets.find(
        (ts) => ts.carId === carId && ts.status === TIRE_SET_STATUS.ACTIVE
      );
    },
    [tireSets]
  );

  const getStoredForCar = useCallback(
    (carId: string): TireSet[] => {
      return tireSets.filter(
        (ts) => ts.carId === carId && ts.status === TIRE_SET_STATUS.STORED
      );
    },
    [tireSets]
  );

  const isEmpty = useMemo(
    () => isLoaded && tireSets.length === 0,
    [isLoaded, tireSets]
  );

  // ==========================================
  // Effects
  // ==========================================

  // Update filter when carId prop changes
  useEffect(() => {
    if (carId) {
      setFilter((prev) => ({
        ...prev,
        carId: [carId],
      }));
    }
  }, [carId]);

  // Handle user change - reset when user changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount
    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list();
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list();
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      tireSets,
      selectedTireSet,
      isLoading,
      isLoaded,
      isEmpty,

      // Operations state
      isCreating,
      isUpdating,
      isRemoving,
      isSwapping,

      // Filter and pagination
      filter,
      pagination,
      sortBy,
      setFilter,
      setPagination,
      setSortBy,

      // Core operations
      list,
      get,
      getMany,
      create,
      update,
      remove,
      removeMany,
      swap,

      // Convenience methods
      refresh,
      select,
      clearSelection,

      // Helper operations
      reset,

      // Derived data helpers
      getActiveForCar,
      getStoredForCar,

      // Error handling
      lastError,
      clearError,
    }),
    [
      tireSets,
      selectedTireSet,
      isLoading,
      isLoaded,
      isEmpty,
      isCreating,
      isUpdating,
      isRemoving,
      isSwapping,
      filter,
      pagination,
      sortBy,
      list,
      get,
      getMany,
      create,
      update,
      remove,
      removeMany,
      swap,
      refresh,
      select,
      clearSelection,
      reset,
      getActiveForCar,
      getStoredForCar,
      lastError,
      clearError,
    ]
  );
};